import { Component } from '@theme/component';

/** Pointer travel (px) before a mouse press becomes a drag. */
const DRAG_THRESHOLD = 5;

/**
 * @typedef {object} EssorSliderRefs
 * @property {HTMLElement} track - Horizontally scrollable list (`ul`) whose `li` children are the slides.
 * @property {HTMLButtonElement} [previous] - Scrolls one slide toward the start.
 * @property {HTMLButtonElement} [next] - Scrolls one slide toward the end.
 * @property {HTMLElement} [progress] - Progress bar; receives `--essor-slider-progress-size` and
 *   `--essor-slider-progress-shift` custom properties.
 */

/**
 * Enhances a natively scrollable, scroll-snapping list with arrow buttons, a progress bar and
 * mouse drag-to-scroll. Without JavaScript the track keeps scrolling with touch, trackpad and
 * keyboard (the track is focusable).
 *
 * Markup contract:
 * ```html
 * <essor-slider-component>
 *   <button ref="previous" on:click="/previous" aria-label="…" aria-controls="Track">…</button>
 *   <button ref="next" on:click="/next" aria-label="…" aria-controls="Track">…</button>
 *   <ul ref="track" id="Track" role="list" tabindex="0" aria-label="…"><li>…</li></ul>
 *   <div ref="progress" aria-hidden="true"><span></span></div>
 * </essor-slider-component>
 * ```
 *
 * Sets `data-scrollable` on the host while the track overflows, and `data-dragging` on the track
 * during a mouse drag.
 *
 * @extends {Component<EssorSliderRefs>}
 */
export class EssorSliderComponent extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number} */
  #frame = 0;

  /** @type {number | undefined} */
  #clickTimer;

  /** @type {{ pointerId: number, startX: number, startScroll: number, active: boolean } | null} */
  #drag = null;

  #suppressClick = false;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  /**
   * Scrolls one slide toward the start. Bound with `on:click="/previous"`.
   */
  previous() {
    this.#scrollBySlides(-1);
  }

  /**
   * Scrolls one slide toward the end. Bound with `on:click="/next"`.
   */
  next() {
    this.#scrollBySlides(1);
  }

  /**
   * Scrolls the track so that the given slide (or an element inside it) is at the start edge.
   *
   * @param {Element} element - A slide or a descendant of one.
   */
  scrollToSlide(element) {
    const { track } = this.refs;
    if (!(track instanceof HTMLElement) || !track.contains(element) || element === track) return;

    const slide = /** @type {Element} */ (
      [...track.children].find((child) => child.contains(element)) ?? element
    );
    const trackRect = track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const delta = this.#isRtl() ? slideRect.right - trackRect.right : slideRect.left - trackRect.left;

    track.scrollBy({ left: delta, behavior: this.#behavior() });
  }

  #setup() {
    this.#teardown();

    const { track } = this.refs;
    if (!(track instanceof HTMLElement)) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    track.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    track.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    track.addEventListener('pointermove', this.#handlePointerMove, { signal });
    track.addEventListener('pointerup', this.#handlePointerUp, { signal });
    track.addEventListener('pointercancel', this.#handlePointerCancel, { signal });
    track.addEventListener('lostpointercapture', this.#handlePointerCancel, { signal });
    track.addEventListener('dragstart', this.#handleDragStart, { signal });
    // Window capture runs before the document-level declarative `on:click` handlers.
    window.addEventListener('click', this.#handleClickCapture, { capture: true, signal });
    this.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
    this.#resizeObserver.observe(track);

    this.#update();
  }

  #teardown() {
    this.#controller?.abort();
    this.#controller = undefined;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;

    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = 0;

    window.clearTimeout(this.#clickTimer);
    this.#suppressClick = false;

    if (this.#drag) {
      this.#drag = null;
      this.refs.track?.removeAttribute('data-dragging');
    }
  }

  /** @returns {ScrollBehavior} */
  #behavior() {
    return this.#reducedMotion.matches ? 'auto' : 'smooth';
  }

  #isRtl() {
    const { track } = this.refs;
    return track instanceof HTMLElement && getComputedStyle(track).direction === 'rtl';
  }

  /**
   * @param {number} delta - Number of slides to move; negative moves toward the start.
   */
  #scrollBySlides(delta) {
    const { track } = this.refs;
    if (!(track instanceof HTMLElement)) return;

    const slide = track.firstElementChild;
    if (!(slide instanceof HTMLElement)) return;

    const styles = getComputedStyle(track);
    const gap = parseFloat(styles.columnGap) || 0;
    const step = slide.getBoundingClientRect().width + gap;
    const direction = styles.direction === 'rtl' ? -1 : 1;

    track.scrollBy({ left: step * delta * direction, behavior: this.#behavior() });
  }

  #scheduleUpdate = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    const { track, previous, next, progress } = this.refs;
    if (!(track instanceof HTMLElement)) return;

    const { scrollWidth, clientWidth } = track;
    const max = Math.max(0, scrollWidth - clientWidth);
    // RTL scroll positions run from 0 to -max; Math.abs normalizes both directions.
    const position = Math.min(max, Math.abs(track.scrollLeft));
    const scrollable = max > 1;

    this.toggleAttribute('data-scrollable', scrollable);
    this.#setDisabled(previous, !scrollable || position <= 1);
    this.#setDisabled(next, !scrollable || position >= max - 1);

    if (progress instanceof HTMLElement) {
      const size = scrollWidth > 0 ? Math.min(1, clientWidth / scrollWidth) : 1;
      const shift = clientWidth > 0 ? position / clientWidth : 0;

      progress.style.setProperty('--essor-slider-progress-size', size.toFixed(4));
      progress.style.setProperty('--essor-slider-progress-shift', shift.toFixed(4));
    }
  }

  /**
   * Uses `aria-disabled` so a focused arrow keeps focus when it reaches the end of the track.
   *
   * @param {HTMLElement | undefined} button
   * @param {boolean} disabled
   */
  #setDisabled(button, disabled) {
    if (!(button instanceof HTMLButtonElement)) return;
    button.setAttribute('aria-disabled', String(disabled));
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const { track } = this.refs;
    if (!(track instanceof HTMLElement) || track.scrollWidth <= track.clientWidth) return;

    this.#drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScroll: track.scrollLeft,
      active: false,
    };
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    // The button was released outside the window.
    if (event.buttons === 0) {
      this.#endDrag();
      return;
    }

    const { track } = this.refs;
    if (!(track instanceof HTMLElement)) return;

    const delta = event.clientX - drag.startX;

    if (!drag.active) {
      if (Math.abs(delta) < DRAG_THRESHOLD) return;

      drag.active = true;
      track.setAttribute('data-dragging', '');
      window.getSelection()?.removeAllRanges();

      try {
        track.setPointerCapture(drag.pointerId);
      } catch {
        // The pointer is no longer active; the drag still works while it stays over the track.
      }
    }

    event.preventDefault();
    // scrollLeft is a physical offset, so the same formula works in LTR and RTL.
    track.scrollLeft = drag.startScroll - delta;
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.pointerId) return;
    this.#endDrag();
  };

  /** @param {PointerEvent} event */
  #handlePointerCancel = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.pointerId) return;
    this.#endDrag();
  };

  #endDrag() {
    const drag = this.#drag;
    this.#drag = null;
    if (!drag?.active) return;

    const { track } = this.refs;
    track?.removeAttribute('data-dragging');

    if (track instanceof HTMLElement && track.hasPointerCapture(drag.pointerId)) {
      track.releasePointerCapture(drag.pointerId);
    }

    // Swallow the click that follows the drag, then reset if no click arrives.
    this.#suppressClick = true;
    window.clearTimeout(this.#clickTimer);
    this.#clickTimer = window.setTimeout(() => {
      this.#suppressClick = false;
    }, 0);
  }

  /** @param {MouseEvent} event */
  #handleClickCapture = (event) => {
    if (!this.#suppressClick) return;

    const { track } = this.refs;
    if (!(event.target instanceof Node) || !(track instanceof HTMLElement) || !track.contains(event.target)) return;

    this.#suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /** @param {DragEvent} event */
  #handleDragStart = (event) => {
    // Native link and image dragging would cancel the pointer stream of a mouse drag.
    event.preventDefault();
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    if (event.target instanceof Element) this.scrollToSlide(event.target);
  };
}

if (!customElements.get('essor-slider-component')) {
  customElements.define('essor-slider-component', EssorSliderComponent);
}
